import type { CatalogEntity } from './types';

// Textos de Configuración › Catálogos por catálogo: una sola voz para botones,
// estados vacíos, confirmaciones y toasts (UX-COPY-01, §E.5). Cada texto nombra el
// elemento y la consecuencia real; nunca "Operación exitosa".

export interface EntityCopy {
  /** "tipo de documento" — en minúscula, para componer frases. */
  noun: string;
  /** Plural en minúscula para los estados vacíos. */
  plural: string;
  newLabel: string;
  newTitle: string;
  editTitle: string;
  createLabel: string;
  emptyTitle: string;
  emptyBody: string;
  /** "Ningún tipo de documento desactivado". */
  noneWith: (status: 'activo' | 'desactivado') => string;
  created: (label: string) => string;
  deactivateTitle: string;
  deactivateMessage: (label: string) => string;
  deactivated: (label: string) => string;
  reactivated: (label: string) => string;
}

export const ENTITY_COPY: Record<CatalogEntity, EntityCopy> = {
  document_type: {
    noun: 'tipo de documento',
    plural: 'tipos de documento',
    newLabel: 'Nuevo tipo',
    newTitle: 'Nuevo tipo de documento',
    editTitle: 'Editar tipo de documento',
    createLabel: 'Crear tipo',
    emptyTitle: 'Todavía no hay tipos de documento',
    emptyBody: 'Por ejemplo: procedimiento, registro o manual.',
    noneWith: (s) => `Ningún tipo de documento ${s}`,
    created: (label) => `Tipo de documento «${label}» creado`,
    deactivateTitle: 'Desactivar tipo de documento',
    deactivateMessage: (label) =>
      `¿Desactivar el tipo de documento «${label}»? Ya no se ofrecerá al dar de alta documentos. Los documentos que ya lo usan no cambian y puedes reactivarlo cuando quieras.`,
    deactivated: (label) => `«${label}» desactivado. Ya no se ofrece en documentos nuevos.`,
    reactivated: (label) => `«${label}» reactivado`,
  },
  process_area: {
    noun: 'proceso',
    plural: 'procesos',
    newLabel: 'Nuevo proceso',
    newTitle: 'Nuevo proceso',
    editTitle: 'Editar proceso',
    createLabel: 'Crear proceso',
    emptyTitle: 'Todavía no hay procesos',
    emptyBody: 'Macroprocesos y subprocesos de la organización.',
    noneWith: (s) => `Ningún proceso ${s}`,
    created: (label) => `Proceso «${label}» creado`,
    deactivateTitle: 'Desactivar proceso',
    deactivateMessage: (label) =>
      `¿Desactivar el proceso «${label}»? Ya no se ofrecerá al dar de alta documentos. Sus subprocesos y los documentos que ya lo usan no cambian, y puedes reactivarlo cuando quieras.`,
    deactivated: (label) => `«${label}» desactivado. Ya no se ofrece en documentos nuevos.`,
    reactivated: (label) => `«${label}» reactivado`,
  },
  norm: {
    noun: 'cláusula',
    plural: 'cláusulas',
    newLabel: 'Nueva cláusula',
    newTitle: 'Nueva cláusula',
    editTitle: 'Editar cláusula',
    createLabel: 'Agregar cláusula',
    emptyTitle: 'Todavía no hay normas ni cláusulas',
    emptyBody: 'Por ejemplo: ISO 9001 7.5.',
    noneWith: (s) => `Ninguna cláusula ${s === 'activo' ? 'activa' : 'desactivada'}`,
    created: (label) => `Cláusula ${label} agregada`,
    deactivateTitle: 'Desactivar cláusula',
    deactivateMessage: (label) =>
      `¿Desactivar la cláusula ${label}? Ya no se podrá usar para clasificar documentos. Las clasificaciones existentes se conservan y puedes reactivarla cuando quieras.`,
    deactivated: (label) => `Cláusula ${label} desactivada. Ya no se usa para clasificar.`,
    reactivated: (label) => `Cláusula ${label} reactivada`,
  },
  custom_field: {
    noun: 'campo propio',
    plural: 'campos propios',
    newLabel: 'Nuevo campo',
    newTitle: 'Nuevo campo propio',
    editTitle: 'Editar campo propio',
    createLabel: 'Crear campo',
    emptyTitle: 'Todavía no hay campos propios',
    emptyBody: 'Por ejemplo: ubicación, protección o departamentos relacionados.',
    noneWith: (s) => `Ningún campo propio ${s}`,
    created: (label) => `Campo «${label}» creado`,
    deactivateTitle: 'Desactivar campo propio',
    deactivateMessage: (label) =>
      `¿Desactivar el campo «${label}»? Deja de mostrarse y de pedirse en los documentos. Los valores ya guardados en los documentos se conservan y puedes reactivarlo cuando quieras.`,
    deactivated: (label) => `Campo «${label}» desactivado. Ya no se muestra en los documentos.`,
    reactivated: (label) => `Campo «${label}» reactivado`,
  },
};

export const savedChanges = (label: string): string => `Cambios guardados en «${label}»`;
